import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import './ModernTable.css';
import SubmitUpdateForm from './submitupdatesmall'; // Import the SubmitUpdateForm component

const ModernTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [showLocationFilters, setShowLocationFilters] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // Hard-coded to always filter for private courses
  const activeFilter = 'private';

  // Fetch data using the new API endpoint
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching data from /clubs-for-courses2/ endpoint...');

        // Use the same endpoint as Courses component
        const response = await axiosInstance.get('/clubs-for-courses2/');

        // Extract clubs data from the response
        const clubsData = response.data.clubs || response.data;

        if (!Array.isArray(clubsData)) {
          throw new Error('API did not return an array of clubs');
        }

        // Transform club data to match the expected course structure for ModernTable
        const standardizedData = clubsData.map(club => ({
          // Use club_id as the primary identifier
          id: club.club_id || club.id,
          club_id: club.club_id || club.id,
          slug: club.slug,

          // Course information
          golf_course: club.name || club.golf_course || 'Unknown Course',
          city: club.city || 'Unknown',
          state: club.state || 'Unknown',
          access_type: club.access_type || 'private',

          // Course details
          number_of_holes: club.number_of_holes || null,
          year_founded: club.year_founded || null,

          // Financial data - map from new API structure
          initiation_fee: club.initiation_fee !== undefined ? club.initiation_fee : null,
          golf_dues: club.annual_dues !== undefined ? club.annual_dues : null, // Map annual_dues to golf_dues

          // Website information
          course_website: club.website || null,
        }));

        // Deduplicate by golf_course|city|state
        const uniqueMap = new Map();
        standardizedData.forEach(course => {
          const key = `${course.golf_course}|${course.city}|${course.state}`;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, course);
          }
        });
        const uniqueData = Array.from(uniqueMap.values());

        setData(uniqueData);
        console.log(`✅ Successfully loaded ${uniqueData.length} clubs from new API`);

      } catch (err) {
        console.error('Error fetching data from /clubs-for-courses2/:', err);
        setError(err.message || 'Failed to load golf courses data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get unique states and cities for filters
  const uniqueStates = useMemo(() => {
    // Normalize states to uppercase and get unique values
    const states = [...new Set(data.map(course => course.state.toUpperCase()))].filter(state => state !== 'UNKNOWN');
    return ['all', ...states.sort()];
  }, [data]);

  const uniqueCities = useMemo(() => {
    let cities;
    if (stateFilter === 'all') {
      cities = [...new Set(data.map(course => course.city))].filter(city => city !== 'Unknown');
    } else {
      // Filter by normalized state (case-insensitive comparison)
      cities = [...new Set(data.filter(course => course.state.toUpperCase() === stateFilter).map(course => course.city))].filter(city => city !== 'Unknown');
    }

    // Create a map to track normalized cities and avoid duplicates
    const cityMap = new Map();
    cities.forEach(city => {
      const normalized = city.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');

      // Only add if we haven't seen this normalized version before
      if (!cityMap.has(normalized)) {
        cityMap.set(normalized, city); // Store the original city for filtering
      }
    });

    return ['all', ...Array.from(cityMap.keys()).sort()];
  }, [data, stateFilter]);

  // Apply all filters - now always filtering for private courses
  const filteredData = useMemo(() => {
    return data.filter(course => {
      // Hard-coded to only show private courses
      const accessTypeMatch = course.access_type && course.access_type.toLowerCase() === 'private';
      // Case-insensitive state comparison
      const stateMatch = stateFilter === 'all' || course.state.toUpperCase() === stateFilter;
      // Case-insensitive city comparison with normalization
      const normalizedCourseCity = course.city.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      const cityMatch = cityFilter === 'all' || normalizedCourseCity === cityFilter;
      return accessTypeMatch && stateMatch && cityMatch;
    });
  }, [data, stateFilter, cityFilter]);

  // Handle state filter change
  const handleStateFilterChange = (state) => {
    setStateFilter(state);
    setCityFilter('all');
  };

  // Toggle location filters visibility
  const toggleLocationFilters = () => {
    setShowLocationFilters(!showLocationFilters);
  };

  // Handle opening the update form - pass club_id for the club field
  const handleOpenUpdateForm = (clubId, e) => {
    e.stopPropagation();
    console.log('Opening update form for club_id:', clubId);
    setSelectedCourseId(clubId); // This is the club_id (UUID)
    setShowUpdateForm(true);
  };

  // Handle closing the update form
  const handleCloseUpdateForm = () => {
    setShowUpdateForm(false);
    setSelectedCourseId(null);
  };

  // Helper function to generate club URL (same as Courses component)
  const getClubUrl = (course) => {
    // Use slug if available, fallback to club_id
    if (course.slug) {
      return `/club/${course.slug}`;
    } else if (course.club_id) {
      return `/club/${course.club_id}`; // This will redirect to slug URL
    } else {
      return `/course/${course.id}`; // Legacy fallback
    }
  };

  // Column definitions (updated to use new URL structure)
  const columns = useMemo(() => [
    {
      accessorKey: 'golf_course',
      header: 'Golf Course',
      cell: info => (
        <div className="course-info2">
          <span className="course-name2">{info.getValue()}</span>
        </div>
      ),
      size: 100,
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: info => {
        const city = info.getValue();
        // Capitalize first letter of each word
        return city.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
      },
      size: 80,
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: info => {
        const state = info.getValue();
        // Convert to uppercase
        return state.toUpperCase();
      },
      size: 60,
    },
    {
      accessorKey: 'initiation_fee',
      header: 'Initiation Fee',
      cell: info => {
        const fee = info.getValue();
        if (fee === null || fee === undefined || fee === 'N/A') return '-';

        // Handle numeric values
        if (typeof fee === 'number') {
          return `$${fee.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
        }

        // Handle string values that might be numeric
        if (typeof fee === 'string') {
          const parsed = parseFloat(fee.replace(/[^0-9.-]/g, ''));
          if (!isNaN(parsed)) {
            return `$${parsed.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
          }
        }

        return '-';
      },
      size: 120,
    },
    {
      accessorKey: 'golf_dues',
      header: 'Annual Dues',
      cell: info => {
        const dues = info.getValue();
        if (dues === null || dues === undefined || dues === 'N/A') return '-';

        // Handle numeric values
        if (typeof dues === 'number') {
          return `$${dues.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
        }

        // Handle string values that might be numeric
        if (typeof dues === 'string') {
          const parsed = parseFloat(dues.replace(/[^0-9.-]/g, ''));
          if (!isNaN(parsed)) {
            return `$${parsed.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
          }
        }

        return '-';
      },
      size: 120,
    },
    {
      id: 'actions',
      header: '',
      cell: info => (
        <div className="actions-wrapper">
          <button
            className="update-link"
            onClick={(e) => handleOpenUpdateForm(info.row.original.club_id, e)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon2" viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </button>
        </div>
      ),
      size: 100,
    },
  ], []);

  // Set up the table instance
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
      sorting: [
        {
          id: 'golf_course',
          desc: false
        }
      ]
    },
  });

  const handleRowClick = (row) => {
    // Use the new URL structure
    const clubUrl = getClubUrl(row.original);
    window.location.href = clubUrl;
  };

  // Render error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // Display loading state
  if (loading) {
    return (
        <div className="pcd-loading-container">
          <div className="pcd-loading-spinner"></div>
          <p>Loading data...</p>
        </div>
    );
  }

  // Check if we have any data to display
  if (data.length === 0) {
    return (
        <div className="empty-state">
          <div className="empty-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        </div>
        <h3>No Golf Courses Found</h3>
        <p>There are no golf courses in our database matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="golf-courses-container">
      {/* Table Header with Filters - Access type filters removed */}
      <div className="table-header">
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search golf courses..."
              value={globalFilter || ''}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>
          <button
            className={`customize-button ${showLocationFilters ? 'active' : ''}`}
            onClick={toggleLocationFilters}
          >
            Location Filters
          </button>
        </div>
      </div>

      {/* Location Filters Section */}
      {showLocationFilters && (
        <div className="location-filters">
          <div className="filter-group">
            <label className="filter-label">State:</label>
            <select
              className="filter-select"
              value={stateFilter}
              onChange={(e) => handleStateFilterChange(e.target.value)}
            >
              {uniqueStates.map(state => (
                <option key={state} value={state}>
                  {state === 'all' ? 'All States' : state}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">City:</label>
            <select
              className="filter-select"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              disabled={stateFilter === 'all'}
            >
              {uniqueCities.map(city => (
                <option key={city} value={city}>
                  {city === 'all' ? 'All Cities' : city}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-status">
            {filteredData.length} private courses found
            {stateFilter !== 'all' && ` in ${stateFilter}`}
            {cityFilter !== 'all' && `, ${cityFilter}`}
          </div>
        </div>
      )}

      {/* Table Content */}
      <div className="table-container">
        <table className="golf-courses-table">
          <thead>
            <tr>
              {table.getHeaderGroups().map(headerGroup => (
                headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={`table-header-cell ${header.id === 'actions' ? 'actions-header' : ''}`}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  >
                    <div className="header-content">
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      {header.id !== 'actions' && (
                        <span className="sort-indicator">
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted()] ?? null}
                        </span>
                      )}
                    </div>
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="table-row"
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`table-cell ${cell.column.id === 'actions' ? 'actions-cell' : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-results">
                  No private golf courses found matching your search criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-container">
        <div className="pagination-info">
          <span>
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
            {' - '}
            {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}
            {' of '}
            {table.getFilteredRowModel().rows.length} private courses
          </span>
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            ‹
          </button>
          <span className="pagination-current">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <button
            className="pagination-button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            ›
          </button>
        </div>
        <div className="page-size-selector">
          <span>Rows per page:</span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Update Form Modal */}
      {showUpdateForm && selectedCourseId && (
        <SubmitUpdateForm
          courseId={selectedCourseId} // This is the club_id UUID that will be passed to the club_id field
          courseName={data.find(c => c.club_id === selectedCourseId)?.golf_course || 'Unknown Course'}
          onClose={handleCloseUpdateForm}
        />
      )}

    </div>
  );
};

export default ModernTable;